import { type KeyboardEvent, type MouseEvent, type ReactNode, useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

import { ICONS } from '@/components/icons/icons'

import styles from './Modal.module.css'

interface ModalProps {
  title: string
  subtitle?: ReactNode
  onClose: () => void
  /** `sm` for dialogs and forms; `md` (default) for tables. */
  size?: 'sm' | 'md'
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function overlayRoot(): HTMLElement {
  return document.getElementById('overlay-root') ?? document.body
}

/**
 * Accessible dialog rendered through a portal: Escape and backdrop click close it, Tab is trapped
 * inside, focus moves in on open (the `[data-autofocus]` element, else the close button) and returns
 * to the opener on close.
 */
export function Modal({ title, subtitle, onClose, size = 'md', children }: ModalProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const preferred = panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')
    const target = preferred ?? closeRef.current
    target?.focus()
    if (target instanceof HTMLInputElement) target.setSelectionRange(target.value.length, target.value.length)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
      opener?.focus?.()
    }
  }, [])

  const handleBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose()
    },
    [onClose],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  return createPortal(
    <div className={styles.backdrop} onMouseDown={handleBackdropClick} onKeyDown={handleKeyDown}>
      <div
        className={`${styles.panel} ${size === 'sm' ? styles.small : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        <div className={styles.head}>
          <div>
            <h3 className={styles.title} id={titleId}>
              {title}
            </h3>
            {subtitle ? <div className={styles.sub}>{subtitle}</div> : null}
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close" ref={closeRef}>
            <ICONS.close size={18} />
          </button>
        </div>
        <div className={styles.content}>{children}</div>
      </div>
    </div>,
    overlayRoot(),
  )
}
